import { useMemo, useState } from "react";
import { useParams } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { QueryPendingState } from "@/api/QueryPendingState";
import { useTenantPackFileQuery, useTenantPackFilesQuery } from "@/api/tenants";
import { useCapability } from "@/auth/SessionContext";
import { filterFiles, layerNote } from "@/lib/packFiles";
import { PageHeader } from "@/shell/PageHeader";
import { Card, CardBody } from "@/ui/Card";
import { Input } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { DeniedState } from "@/ui/states";
import { FileEditor } from "@/ui/files/FileEditor";
import { FileTree } from "@/ui/files/FileTree";

/**
 * #1165: the pack files a preview target runs now, read-only. Opened from the
 * Projects page's Tenants tab. Nothing here saves, reverts or compares: the
 * editor's own Project pages own those.
 */
export function TenantFilesRoute() {
  const { tenantId = "" } = useParams();
  const read = useCapability("project.read");

  const list = useTenantPackFilesQuery(tenantId);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const file = useTenantPackFileQuery(tenantId, selected);

  const items = list.data?.items;
  const files = useMemo(() => filterFiles(items ?? [], query), [items, query]);

  if (!read.allowed) {
    return <DeniedState description={read.reason ?? undefined} />;
  }
  if (list.isPending) {
    return (
      <QueryPendingState
        isPaused={list.isPaused}
        failureReason={list.failureReason}
        queryKey={queryKeys.tenantPackFiles(tenantId)}
        label="Loading pack files"
        errorTitle="Pack files could not be loaded"
      />
    );
  }
  if (list.isError) {
    return (
      <QueryErrorState
        error={list.error}
        onRetry={() => void list.refetch()}
        title="Pack files could not be loaded"
        subject="Pack files"
      />
    );
  }

  const data = file.data;
  const tenantName = list.data.tenantPack?.name ?? null;
  const stack = [list.data.basePack, list.data.tenantPack]
    .flatMap((ref) =>
      ref ? [ref.revision ? `${ref.name} @${ref.revision}` : ref.name] : [],
    )
    .join(" + ");

  return (
    <div className="space-y-4">
      <PageHeader
        breadcrumbs={[
          { label: "Projects", to: "/projects" },
          { label: "Tenants", to: "/projects?view=tenants" },
          { label: tenantId },
        ]}
        title={`${tenantId} pack files`}
        description={`The files ${tenantId} runs now. Read-only.`}
        meta={<span className="font-medium">{stack}</span>}
      />

      {!list.data.baseAvailable ? (
        <Notice>
          The base pack is not available here, so only tenant and edited files
          are listed.
        </Notice>
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-[17.5rem_minmax(0,1fr)]">
        <Card>
          <CardBody className="space-y-2">
            <Input
              type="search"
              aria-label="Search files"
              placeholder="Search files"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            {files.length === 0 ? (
              <p className="text-sm text-muted-foreground">No files match.</p>
            ) : (
              <FileTree
                files={files}
                query={query}
                selected={selected}
                onSelect={setSelected}
                tenantName={tenantName ?? undefined}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardBody className="space-y-3">
            {selected === null ? (
              <p className="text-sm text-muted-foreground">
                Pick a file on the left to read it.
              </p>
            ) : file.isPending ? (
              <QueryPendingState
                isPaused={file.isPaused}
                failureReason={file.failureReason}
                queryKey={queryKeys.tenantPackFile(tenantId, selected)}
                label="Loading file"
                errorTitle="This file could not be loaded"
              />
            ) : file.isError ? (
              <QueryErrorState
                error={file.error}
                onRetry={() => void file.refetch()}
                title="This file could not be loaded"
                subject="Pack files"
              />
            ) : data ? (
              <>
                <p dir="ltr" className="break-all font-mono text-sm font-medium">
                  {data.path}
                </p>
                <p className="text-xs text-muted-foreground">
                  {layerNote(data.layer, data.path, tenantName ?? "tenant")}
                </p>
                <FileEditor
                  label={`Content of ${data.path}`}
                  value={data.content}
                  readOnly
                  onChange={() => {}}
                  onSave={() => {}}
                />
              </>
            ) : null}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
